import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, X } from "lucide-react";
import { toast } from "sonner";

import { Checkbox } from "@/components/ui/checkbox";
import type { PMIssueChecklistItem } from "@/domains/pm/types";
import { isPendingChecklistItem, useIssueChecklist } from "@/domains/pm/hooks/useIssueChecklist";
import { parseChecklistInput } from "@/domains/pm/utils/checklist";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { CHECKLIST_ITEM_MAX_LENGTH } from "@/stores/pm/repository";
import { PanelSectionLabel } from "./PanelSectionLabel";

interface IssueChecklistSectionProps {
  issueId: number;
  /** Read the list without being able to change it — snapshot mode. */
  readOnly?: boolean;
  /**
   * The user chose "Add checklist" (the panel's "+" menu or the right-click
   * menu). An issue without items shows no Checklist section until then.
   */
  requested?: boolean;
  /** Bumped on every "Add checklist" choice: focuses the composer again. */
  focusToken?: number;
  /** The composer was left empty on an issue with no items: hide again. */
  onDismiss?: () => void;
}

/**
 * The Checklist band of the issue detail panel: short items to tick off,
 * dragged into order, renamed in place. Shown only once the issue has items or
 * the user asked for a checklist; hidden while the table is not deployed.
 */
export function IssueChecklistSection({
  issueId,
  readOnly = false,
  requested = false,
  focusToken = 0,
  onDismiss,
}: IssueChecklistSectionProps) {
  const { t } = useLanguage();
  const copy = t.pm.issues.checklist;
  const checklist = useIssueChecklist(issueId);
  const { items } = checklist;
  const [draft, setDraft] = useState("");
  const composerRef = useRef<HTMLInputElement>(null);

  // The composer mounts in the same render the request arrives in; focus it
  // on the next frame, and again whenever "Add checklist" is chosen anew.
  useEffect(() => {
    if (!requested || focusToken === 0) return;
    const frame = requestAnimationFrame(() => composerRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [requested, focusToken]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (!checklist.available) return null;
  if (items.length === 0 && (readOnly || !requested)) return null;

  const done = items.filter((i) => i.is_done).length;
  const total = items.length;
  const anyPending = items.some(isPendingChecklistItem);
  const showComposer = !readOnly;

  const submit = (texts: string[]) => {
    if (texts.length === 0) return;
    if (texts.some((text) => text.length > CHECKLIST_ITEM_MAX_LENGTH)) {
      toast.error(copy.tooLong);
      return;
    }
    void checklist.add(texts);
    setDraft("");
  };

  const onComposerKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      // Focus stays put, so a list can be typed one line after another.
      submit(parseChecklistInput(draft));
    } else if (e.key === "Escape") {
      e.stopPropagation();
      setDraft("");
      if (total === 0) onDismiss?.();
    }
  };

  // A text input drops line breaks from a paste, so a pasted list would
  // arrive as one run-on item. Take multi-line pastes whole, one item a line.
  const onComposerPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    if (!/\r?\n/.test(pasted.trim())) return;
    e.preventDefault();
    submit(parseChecklistInput(`${draft}${pasted}`));
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const ids = items.map((i) => i.id);
    const from = ids.indexOf(Number(active.id));
    const to = ids.indexOf(Number(over.id));
    if (from < 0 || to < 0) return;
    void checklist.reorder(arrayMove(ids, from, to));
  };

  return (
    <div className="py-2.5">
      <div className="flex items-center justify-between gap-3">
        <PanelSectionLabel>
          {copy.title}
          {total > 0 && (
            <span> · {fill(copy.progress, { done: String(done), total: String(total) })}</span>
          )}
        </PanelSectionLabel>
      </div>

      {total > 0 && (
        <div
          className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-label={fill(copy.progressLabel, { done: String(done), total: String(total) })}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={done}
        >
          <div
            className={cn(
              "h-full rounded-full transition-[width] duration-300",
              done === total ? "bg-green-600" : "bg-primary"
            )}
            style={{ width: `${(done / total) * 100}%` }}
          />
        </div>
      )}

      {checklist.loadFailed && total === 0 && (
        <p className="mt-1.5 text-xs text-muted-foreground">{copy.loadFailed}</p>
      )}

      {total > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <ul className="mt-1.5 space-y-px">
              {items.map((item) => (
                <ChecklistRow
                  key={item.id}
                  item={item}
                  readOnly={readOnly}
                  reorderDisabled={readOnly || anyPending}
                  onToggle={(value) => void checklist.setDone(item.id, value)}
                  onRename={(text) => void checklist.rename(item.id, text)}
                  onRemove={() => void checklist.remove(item.id)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {showComposer && (
        <div className="mt-1 flex items-center gap-2 pl-5">
          <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <input
            ref={composerRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onComposerKeyDown}
            onPaste={onComposerPaste}
            onBlur={() => {
              if (total === 0 && !draft.trim()) onDismiss?.();
            }}
            maxLength={CHECKLIST_ITEM_MAX_LENGTH}
            placeholder={copy.addPlaceholder}
            aria-label={copy.addPlaceholder}
            className="h-7 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
      )}
    </div>
  );
}

interface ChecklistRowProps {
  item: PMIssueChecklistItem;
  readOnly: boolean;
  reorderDisabled: boolean;
  onToggle: (done: boolean) => void;
  onRename: (text: string) => void;
  onRemove: () => void;
}

function ChecklistRow({
  item,
  readOnly,
  reorderDisabled,
  onToggle,
  onRename,
  onRemove,
}: ChecklistRowProps) {
  const { t } = useLanguage();
  const copy = t.pm.issues.checklist;
  const pending = isPendingChecklistItem(item);
  // The text being typed lives here, not in the cache, so a realtime re-read
  // arriving mid-edit cannot overwrite it.
  const [editing, setEditing] = useState<string | null>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: reorderDisabled });

  const commit = () => {
    if (editing === null) return;
    const text = editing.replace(/\s+/g, " ").trim();
    setEditing(null);
    if (text === item.text) return;
    // Emptying an item is how it is deleted from the keyboard.
    if (text === "") onRemove();
    else onRename(text);
  };

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "group flex items-start gap-1 rounded py-0.5 pr-1",
        isDragging && "relative z-10 bg-background shadow-sm"
      )}
    >
      {readOnly ? (
        <span className="w-4 shrink-0" aria-hidden />
      ) : (
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          disabled={reorderDisabled}
          aria-label={copy.reorderItem}
          className="mt-0.5 flex h-5 w-4 shrink-0 cursor-grab items-center justify-center text-muted-foreground opacity-0 transition-opacity focus-visible:opacity-100 active:cursor-grabbing disabled:cursor-default group-hover:opacity-100"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}

      <Checkbox
        checked={item.is_done}
        onCheckedChange={(value) => onToggle(value === true)}
        disabled={readOnly || pending}
        aria-label={item.text}
        className="mt-[3px] h-3.5 w-3.5 [&_svg]:h-3 [&_svg]:w-3"
      />

      {editing !== null ? (
        <input
          autoFocus
          value={editing}
          onChange={(e) => setEditing(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.stopPropagation();
              setEditing(null);
            }
          }}
          maxLength={CHECKLIST_ITEM_MAX_LENGTH}
          aria-label={item.text}
          className="ml-1.5 min-w-0 flex-1 bg-transparent text-sm leading-5 outline-none"
        />
      ) : (
        <span
          role={readOnly || pending ? undefined : "button"}
          tabIndex={readOnly || pending ? undefined : 0}
          onClick={() => !readOnly && !pending && setEditing(item.text)}
          onKeyDown={(e) => {
            if (!readOnly && !pending && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              setEditing(item.text);
            }
          }}
          className={cn(
            "ml-1.5 min-w-0 flex-1 whitespace-pre-wrap break-words text-sm leading-5",
            !readOnly && !pending && "cursor-text",
            item.is_done && "text-muted-foreground line-through",
            pending && "opacity-60"
          )}
        >
          {item.text}
        </span>
      )}

      {!readOnly && editing === null && (
        <button
          type="button"
          onClick={onRemove}
          disabled={pending}
          aria-label={copy.deleteItem}
          className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </li>
  );
}
